import { useState, useCallback, ChangeEvent, FocusEvent } from 'react';
import { i18n } from '../i18n';

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: unknown) => boolean;
  message?: string;
}

export interface ValidationRules {
  [key: string]: ValidationRule;
}

export interface ValidationErrors {
  [key: string]: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useFormValidation<T extends Record<string, any>>(
  initialValues: T,
  validationRules: ValidationRules
) {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isDirty, setIsDirty] = useState(false);

  const validateField = useCallback(
    (name: string, value: unknown): string => {
      const rules = validationRules[name];
      if (!rules) return '';

      // Required validation
      if (rules.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
        return rules.message || i18n.t('validation:required');
      }

      // Min length validation
      if (rules.minLength && typeof value === 'string' && value.length < rules.minLength) {
        return rules.message || i18n.t('validation:minLength', { count: rules.minLength });
      }

      // Max length validation
      if (rules.maxLength && typeof value === 'string' && value.length > rules.maxLength) {
        return rules.message || i18n.t('validation:maxLength', { count: rules.maxLength });
      }

      // Pattern validation
      if (rules.pattern && typeof value === 'string' && !rules.pattern.test(value)) {
        return rules.message || i18n.t('validation:invalidFormat');
      }

      // Custom validation
      if (rules.custom && !rules.custom(value)) {
        return rules.message || i18n.t('validation:invalidValue');
      }

      return '';
    },
    [validationRules]
  );

  const validateAll = useCallback((): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    Object.keys(validationRules).forEach((fieldName) => {
      const error = validateField(fieldName, values[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [values, validationRules, validateField]);

  const handleChange = useCallback(
    (name: string, value: unknown) => {
      setValues((prev) => ({ ...prev, [name]: value }));
      setIsDirty(true);

      // Validate on change if field was touched
      if (touched[name]) {
        const error = validateField(name, value);
        setErrors((prev) => ({
          ...prev,
          [name]: error,
        }));
      }
    },
    [touched, validateField]
  );

  // React event handlers for inputs
  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      handleChange(name, value);
    },
    [handleChange]
  );

  const handleInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name } = e.target;
      setTouched((prev) => ({ ...prev, [name]: true }));
      const error = validateField(name, values[name]);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    },
    [values, validateField]
  );

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsDirty(false);
  }, [initialValues]);

  const setFieldValue = useCallback((name: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
  }, []);

  const setFormValues = useCallback((newValues: Partial<T>) => {
    setValues((prev) => ({ ...prev, ...newValues }));
    setIsDirty(false); // Reset dirty state when programmatically setting values
    setErrors({});
    setTouched({});
  }, []);

  // Merge server-side field errors (typically from ApiError.errors after mapApiFieldErrors).
  // Touches the affected fields so the messages are immediately visible.
  const setServerErrors = useCallback((serverErrors: ValidationErrors) => {
    if (!serverErrors || Object.keys(serverErrors).length === 0) return;
    setErrors((prev) => ({ ...prev, ...serverErrors }));
    setTouched((prev) => {
      const next = { ...prev };
      for (const field of Object.keys(serverErrors)) next[field] = true;
      return next;
    });
  }, []);

  return {
    values,
    errors,
    touched,
    isDirty,
    handleChange,
    handleInputChange,
    handleInputBlur,
    validateAll,
    reset,
    setFieldValue,
    setFormValues,
    setServerErrors,
  };
}
